<template>
  <div
    :style="containerStyle"
    :class="[{
      'checkbox--indeterminate': indeterminate,
      'checkbox--rounded' : rounded
    }, `checkbox-${appearance}`]"
    class="checkbox-container"
  >
    <label class="is-flex align-center">
      <input
        :id="name"
        v-model="checkedModel"
        :value="value"
        :disabled="disabled"
        type="checkbox"
        @change="changeStatus"
      >
      <span
        :style="checkmarkStyle"
        :class="[
          `checkbox-checkmark--${checkmarkSize}`,
          {'checkbox-checkmark--disabled': disabled}
        ]"
        class="checkbox-checkmark"
      />
      <span
        :style="labelStyle"
        :class="{'checked': checkedModel}"
        class="check-label has-margin-left-2"
      >{{ label }}</span>
    </label>
  </div>
</template>

<script>

// Vue 3: l'opzione `model` è rimossa. Si mantiene il prop `checked` e si emette
// `update:checked` (per `v-model:checked`) oltre a `change` (listener espliciti).
export default {
  name: 'CheckBox',
  props: {
    indeterminate: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    value: { type: [String], default: null },
    checked: { type: [Array, Boolean], default: false },
    label: { type: String, default: '' },
    labelStyle: { type: Object, default: () => {} },
    name: { type: String, required: true },
    appearance: {
      type: String,
      default: 'primary',
      validator: val => ['primary', 'secondary', 'tertiary', 'quaternary'].includes(val),
    },
    rounded: { type: Boolean, default: false },
    size: { type: Number, default: 22 },
    checkmarkSize: {
      type: String,
      default: 'normal',
      validator: val => ['normal', 'medium', 'little', 'big', 'xl'].includes(val),
    },
    background: { type: String, default: '' },
    width: { type: String, default: null },
  },
  emits: ['update:checked', 'change'],
  data() {
    return {
      checkedModel: this.checked,
    };
  },
  computed: {
    containerStyle() {
      return Object.assign(this.width !== null && { width: `${this.width}px` });
    },
    checkmarkStyle() {
      return Object.assign(
        {
          height: `${this.size}px`,
          width: `${this.size}px`,
        },
        this.background !== '' && this.checkedModel && { border: `1px solid ${this.background}` },
        this.background !== '' && this.checkedModel && { background: this.background },
      );
    },
  },
  watch: {
    checked(newVal) {
      this.checkedModel = newVal;
    },
  },
  methods: {
    changeStatus(event) {
      if (Array.isArray(this.checked)) {
        this.$emit('update:checked', this.checkedModel);
        this.$emit('change', this.checkedModel);
      } else if (typeof this.checked === 'boolean') {
        this.$emit('update:checked', event.target.checked);
        this.$emit('change', event.target.checked);
      }
    },
  },
};
</script>

<style lang="scss" scoped>

.checkbox-container{
  position: relative;

  &.checkbox-primary {
    .check-label {
      color: $grey-gunsmoke;
    }
    .checkbox-checkmark {
      background: $white;
      border: 1px solid $grey-gunsmoke;
    }
    input[type=checkbox]:checked + label {
      border: 1px solid $grenadier;
      background: $grenadier;
    }
    &.checkbox--indeterminate input[type=checkbox] + label {
      border: 1px solid $grenadier;
      background: $grenadier;
    }
    &:not(.checkbox--indeterminate) .check-label.checked {
      color: $black;
    }

    &:not(.checkbox--indeterminate) .checkbox-checkmark:after {
      border: 1px solid $grey-solitude;
    }

    &:not(.checkbox--indeterminate) .checkbox-checkmark:before {
      border: 1px solid $grey-solitude;
    }

    &.checkbox--indeterminate .checkbox-checkmark:before {
      border: 1px solid $grey-solitude;
    }

  }

  &.checkbox-secondary {
    .check-label {
      font-size: $size-10;
      color: $blue-east-bay;
    }
    .checkbox-checkmark {
      background: $white-old;
      border: 1px solid $grey-border-dark;
    }
    input[type=checkbox]:checked + .checkbox-checkmark {
      border: 1px solid $blue-sky;
      background: $blue-sky;
    }
    &.checkbox--indeterminate input[type=checkbox] + .checkbox-checkmark {
      border: 1px solid $blue-sky;
      background: $blue-sky;
    }

    &:not(.checkbox--indeterminate) .checkbox-checkmark:after {
      border: 1px solid $white;
    }

    &:not(.checkbox--indeterminate) .checkbox-checkmark:before {
      border: 1px solid $white;
    }

    &.checkbox--indeterminate .checkbox-checkmark:before {
      border: 1px solid $white;
    }

  }

  &.checkbox-tertiary {
    .check-label {
      font-size: $size-10;
      color: $platinum;
    }
    .checkbox-checkmark {
      background: $white-old;
      border: 1px solid $grey-border-dark;
    }
    input[type=checkbox]:checked + .checkbox-checkmark {
      border: 1px solid $platinum;
      background: $platinum;
    }
    &.checkbox--indeterminate input[type=checkbox] + .checkbox-checkmark {
      border: 1px solid $platinum;
      background: $platinum;
    }

    &:not(.checkbox--indeterminate) .checkbox-checkmark:after {
      border: 1px solid $spanish-gray;
    }

    &:not(.checkbox--indeterminate) .checkbox-checkmark:before {
      border: 1px solid $spanish-gray;
    }

    &.checkbox--indeterminate .checkbox-checkmark:before {
      border: 1px solid $spanish-gray;
    }

  }

  &.checkbox-quaternary {
    .check-label {
      font-size: $size-10;
      line-height: 15px;
      color: $blue-east-bay;
      margin-left: 0.5rem !important;
    }
    .checkbox-checkmark {
      background: $white-old;
      border: 1px solid $grey-border-dark;
    }
    input[type=checkbox] {
      height: 22px;
      width: 22px;
    }
    input[type=checkbox]:checked + .checkbox-checkmark {
      border: 1px solid $black-gunmetal;
      background: $black-gunmetal;
    }
    &.checkbox--indeterminate input[type=checkbox] + .checkbox-checkmark {
      border: 1px solid $black-gunmetal;
      background: $black-gunmetal;
    }

    &:not(.checkbox--indeterminate) .checkbox-checkmark:after {
      border: 1px solid $white;
    }

    &:not(.checkbox--indeterminate) .checkbox-checkmark:before {
      border: 1px solid $white;
    }

    &.checkbox--indeterminate .checkbox-checkmark:before {
      border: 1px solid $white;
    }
  }


  input[type=checkbox] {
    opacity: 0;
  }

  .check-label {
    cursor: pointer;
  }

  .checkbox-checkmark {
    cursor: pointer;
    position: absolute;
    width: 22px;
    height: 22px;
    top: 0;
    left: 0;
    border-radius: 5px;
    &--disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }

  &:not(.checkbox--indeterminate) .checkbox-checkmark:after {
    content: '';
    opacity: 0;
    position: absolute;
    width: 12px;
    height: 1px;
    top: 9px;
    left: 6px;
    transform: rotate(-45deg);
    border-radius: 2px;
    box-sizing: border-box;
  }
  &:not(.checkbox--indeterminate) .checkbox-checkmark--xl:after {
    content: '';
    opacity: 0;
    position: absolute;
    width: 16px;
    height: 1px;
    top: 17px;
    left: 14px;
    transform: rotate(-45deg);
    border-radius: 2px;
    box-sizing: border-box;
  }
  &:not(.checkbox--indeterminate) .checkbox-checkmark--big:after {
    content: '';
    opacity: 0;
    position: absolute;
    width: 12px;
    height: 1px;
    top: 11px;
    left: 10px;
    transform: rotate(-45deg);
    border-radius: 2px;
    box-sizing: border-box;
  }
  &:not(.checkbox--indeterminate) .checkbox-checkmark--medium:after {
    content: '';
    opacity: 0;
    position: absolute;
    width: 10px;
    height: 1px;
    top: 8px;
    left: 6px;
    transform: rotate(-45deg);
    border-radius: 2px;
    box-sizing: border-box;
  }
  &:not(.checkbox--indeterminate) .checkbox-checkmark--little:after {
    content: '';
    opacity: 0;
    position: absolute;
    width: 9px;
    height: 1px;
    top: 7px;
    left: 6px;
    transform: rotate(-45deg);
    border-radius: 2px;
    box-sizing: border-box;
  }

  &:not(.checkbox--indeterminate) .checkbox-checkmark:before {
    content: '';
    opacity: 0;
    position: absolute;
    width: 1px;
    height: 8px;
    top: 7px;
    left: 5px;
    transform: rotate(-45deg);
    border-radius: 2px;
    box-sizing: border-box;
  }
  &:not(.checkbox--indeterminate) .checkbox-checkmark--xl:before {
    content: '';
    opacity: 0;
    position: absolute;
    width: 1px;
    height: 12px;
    top: 14px;
    left: 12px;
    transform: rotate(-45deg);
    border-radius: 2px;
    box-sizing: border-box;
  }
  &:not(.checkbox--indeterminate) .checkbox-checkmark--big:before {
    content: '';
    opacity: 0;
    position: absolute;
    width: 1px;
    height: 8px;
    top: 10px;
    left: 9px;
    transform: rotate(-45deg);
    border-radius: 2px;
    box-sizing: border-box;
  }
  &:not(.checkbox--indeterminate) .checkbox-checkmark--medium:before {
    content: '';
    opacity: 0;
    position: absolute;
    width: 1px;
    height: 8px;
    top: 6px;
    left: 5px;
    transform: rotate(-45deg);
    border-radius: 2px;
    box-sizing: border-box;
  }
  &:not(.checkbox--indeterminate) .checkbox-checkmark--little:before {
    content: '';
    opacity: 0;
    position: absolute;
    width: 1px;
    height: 7px;
    top: 5px;
    left: 5px;
    transform: rotate(-45deg);
    border-radius: 2px;
    box-sizing: border-box;
  }
  &.checkbox--indeterminate .checkbox-checkmark:before {
    content: '';
    opacity: 0;
    position: absolute;
    width: 12px;
    height: 1px;
    top: 9px;
    left: 4px;
    border-radius: 2px;
    box-sizing: border-box;
  }
  &.checkbox--indeterminate.checkbox--rounded .checkbox-checkmark:before {
    width: 10px;
    height: 10px;
    background-color: $white;
    top: 5px;
    left: 5px;
    border-radius: 50%;
  }

  &.checkbox--indeterminate input[type=checkbox] + .checkbox-checkmark:before {
    opacity: 1;
  }

  &:not(.checkbox--indeterminate) input[type=checkbox]:checked + .checkbox-checkmark:after {
    opacity: 1;
  }
  &:not(.checkbox--indeterminate) input[type=checkbox]:checked + .checkbox-checkmark:before {
    opacity: 1;
  }

  &.checkbox--rounded .checkbox-checkmark{
    border-radius: 50%;
  }

}


</style>
